import "maplibre-gl/dist/maplibre-gl.css";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import MapLoadingPanel from "./components/MapLoadingPanel";
import { COLOR_MODES, HUE_PALETTES } from "../MapControls/data";
import { INDIA_MAP_BOUNDS, MAP_DEFAULT_VIEW, TILE_LABEL_LAYER_URLS, TILE_LAYER_ATTRIBUTION, TILE_LAYER_URLS } from "./data";

const EMPTY_FEATURE_COLLECTION = {
	type: "FeatureCollection",
	features: [],
};

const MASTER_SOURCE_ID = "master-polygons-source";
const ONLINE_SOURCE_ID = "online-polygons-source";
const SKATER_SOURCE_ID = "skater-polygons-source";
const STATE_BORDER_SOURCE_ID = "state-borders-source";
const STORE_SOURCE_ID = "store-points-source";
const COMPETITOR_SOURCE_ID = "competitor-points-source";

const MASTER_FILL_LAYER_ID = "master-polygons-fill";
const MASTER_LINE_LAYER_ID = "master-polygons-line";
const ONLINE_FILL_LAYER_ID = "online-polygons-fill";
const ONLINE_LINE_LAYER_ID = "online-polygons-line";
const SKATER_FILL_LAYER_ID = "skater-polygons-fill";
const SKATER_LINE_LAYER_ID = "skater-polygons-line";
const STATE_BORDER_LAYER_ID = "state-borders-line";
const STORE_EBO_LAYER_ID = "store-ebo-points";
const STORE_MBO_LAYER_ID = "store-mbo-points";
const STORE_LFS_LAYER_ID = "store-lfs-points";
const COMPETITOR_LAYER_ID = "competitor-points";

const getFeatureCount = (featureCollection) => featureCollection?.features?.length ?? 0;

const makePinSvg = (color, letter) =>
	`<svg xmlns="http://www.w3.org/2000/svg" width="64" height="80" viewBox="0 0 64 80"><path d="M32 0C14.327 0 0 14.327 0 32c0 21.333 32 48 32 48S64 53.333 64 32C64 14.327 49.673 0 32 0z" fill="${color}" stroke="#fff" stroke-width="3"/><text x="32" y="30" text-anchor="middle" dominant-baseline="middle" font-family="system-ui,sans-serif" font-size="26" font-weight="900" fill="#fff" stroke="rgba(0,0,0,0.3)" stroke-width="1.5" paint-order="stroke">${letter}</text></svg>`;

const makeCompetitorSvg = (color, letters) =>
	`<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><circle cx="32" cy="32" r="30" fill="${color}" stroke="#fff" stroke-width="2"/><text x="32" y="32" text-anchor="middle" dominant-baseline="middle" font-family="system-ui,sans-serif" font-size="24" font-weight="900" fill="#fff" stroke="rgba(0,0,0,0.3)" stroke-width="1" paint-order="stroke">${letters}</text></svg>`;

const getBrandLetters = (brand = "") => {
	const words = brand.trim().split(/\s+/).filter(Boolean);
	if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
	return brand.slice(0, 2).toUpperCase() || "??";
};

const svgToMapImage = (map, id, svgStr, w, h) =>
	new Promise((resolve) => {
		if (map.hasImage(id)) return resolve();
		const img = new Image(w, h);
		img.onload = () => {
			if (!map.hasImage(id)) map.addImage(id, img, { pixelRatio: 2 });
			resolve();
		};
		img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgStr)}`;
	});

const registerStoreImages = (map, features) =>
	Promise.all(
		[...new Map(features.map((f) => [f.properties.store_kind, f.properties])).values()].map(
			({ store_kind, render_color, marker_label }) =>
				svgToMapImage(
					map,
					`pin-${store_kind}`,
					makePinSvg(render_color || "#888", marker_label || store_kind[0].toUpperCase()),
					64,
					80,
				),
		),
	);

const registerCompetitorImages = (map, features) =>
	Promise.all(
		[...new Map(features.map((f) => [f.properties.brand_display, f.properties])).values()].map(({ brand_display, render_color }) =>
			svgToMapImage(
				map,
				`comp-${brand_display}`,
				makeCompetitorSvg(render_color || "#888", getBrandLetters(brand_display)),
				64,
				64,
			),
		),
	);

const getMetricColor = (value, palette) => {
	if (!palette?.length) return "#5b8def";
	if (value <= 0.25) return palette[0];
	if (value <= 0.5) return palette[1] ?? palette[0];
	if (value <= 0.75) return palette[2] ?? palette[1] ?? palette[0];
	if (value <= 0.9) return palette[3] ?? palette[2] ?? palette[0];
	return palette[4] ?? palette[3] ?? palette[0];
};

const getCoverageBucket = (coverageCount) => {
	if (coverageCount >= 3) return 3;
	if (coverageCount >= 2) return 2;
	if (coverageCount >= 1) return 1;
	return 0;
};

const formatDisplayNumber = (value, digits = 2) => {
	const parsed = Number(value);
	if (!Number.isFinite(parsed)) return "-";
	return parsed.toFixed(digits);
};

const getSkaterFillColor = (properties, colorMode, paletteColors) => {
	if (colorMode === "hue") return getMetricColor(properties.hue_score_norm ?? 0, paletteColors);
	if (colorMode === "priority") return getMetricColor(properties.priority_score_norm ?? 0, paletteColors);
	if (colorMode === "coverage") {
		const coverageColors = {
			0: "#d9d9d9",
			1: "#fee08b",
			2: "#f46d43",
			3: "#1a9850",
		};
		return coverageColors[getCoverageBucket(properties.own_store_type_count ?? 0)] ?? coverageColors[0];
	}
	return properties.cluster_color ?? "#5b8def";
};

const buildMasterRenderData = (featureCollection) => ({
	type: "FeatureCollection",
	features: (featureCollection?.features ?? []).map((feature, index) => ({
		...feature,
		id: feature.id ?? feature?.properties?.pincode ?? `master-${index}`,
		properties: {
			...feature.properties,
			render_fill: "#dbeafe",
			render_line: "#1f2937",
		},
	})),
});

const buildOnlineRenderData = (featureCollection) => ({
	type: "FeatureCollection",
	features: (featureCollection?.features ?? []).map((feature, index) => ({
		...feature,
		id: feature.id ?? feature?.properties?.pincode ?? `online-${index}`,
		properties: {
			...feature.properties,
			render_fill: "#d4d4d8",
			render_line: "#3f3f46",
			score_display: feature?.properties?.score_display ?? formatDisplayNumber(feature?.properties?.smoothed_score),
		},
	})),
});

const buildSkaterRenderData = (featureCollection, colorMode, paletteColors) => ({
	type: "FeatureCollection",
	features: (featureCollection?.features ?? []).map((feature, index) => {
		const properties = feature?.properties ?? {};
		const fillColor = getSkaterFillColor(properties, colorMode, paletteColors);
		return {
			...feature,
			id: feature.id ?? properties.pincode ?? `skater-${index}`,
			properties: {
				...properties,
				render_fill: fillColor,
				render_line: "#1f2937",
				render_line_width: 0.9,
				region_score_display: properties.region_score_display ?? formatDisplayNumber(properties.region_score),
				priority_score_display: properties.priority_score_display ?? formatDisplayNumber(properties.priority_score),
			},
		};
	}),
});

const createBaseMapStyle = () => ({
	version: 8,
	sources: {
		"plain-base": {
			type: "raster",
			tiles: TILE_LAYER_URLS,
			tileSize: 256,
			attribution: TILE_LAYER_ATTRIBUTION,
		},
		"plain-labels": {
			type: "raster",
			tiles: TILE_LABEL_LAYER_URLS,
			tileSize: 256,
			attribution: TILE_LAYER_ATTRIBUTION,
		},
	},
	layers: [
		{
			id: "plain-base",
			type: "raster",
			source: "plain-base",
			paint: {
				"raster-opacity": 1,
				"raster-saturation": -0.82,
				"raster-contrast": 0.08,
				"raster-brightness-min": 0.25,
				"raster-brightness-max": 0.98,
			},
		},
	],
});

const getPopupHtml = (layerId, properties = {}) => {
	if (layerId === MASTER_FILL_LAYER_ID) {
		return `
			<div><strong>${properties.office_name || properties.Office_Name || properties.pincode || "Master polygon"}</strong></div>
			<div>State: ${properties.state || properties.State || "-"}</div>
			<div>Pincode: ${properties.pincode || properties.Pincode || "-"}</div>
			<div>District: ${properties.district || "-"}</div>
		`;
	}

	if (layerId === ONLINE_FILL_LAYER_ID) {
		return `
			<div><strong>Online Polygon</strong></div>
			<div>Pincode: ${properties.pincode || "-"}</div>
			<div>State: ${properties.state || "-"}</div>
			<div>Score: ${properties.score_display || "-"}</div>
			<div>Reason: ${properties.unassigned_reason || "-"}</div>
		`;
	}

	if (layerId === SKATER_FILL_LAYER_ID) {
		return `
			<div><strong>${properties.region_id || "SKATER region"}</strong></div>
			<div>State: ${properties.state || "-"}</div>
			<div>Pincode: ${properties.pincode || "-"}</div>
			<div>Region Score: ${properties.region_score_display || "-"}</div>
			<div>Priority Score: ${properties.priority_score_display || "-"}</div>
			${properties.rank_overall ? `<div>Priority Rank: ${properties.rank_overall}</div>` : ""}
			${properties.top_100_priority_display ? `<div>Top 100 Priority: ${properties.top_100_priority_display}</div>` : ""}
			${properties.own_store_type_count != null ? `<div>Our Store Types Present: ${properties.own_store_type_count}</div>` : ""}
			${properties.own_store_types ? `<div>Our Store Types: ${properties.own_store_types}</div>` : ""}
			${properties.competitor_total_store_count != null ? `<div>Competitor Store Count: ${properties.competitor_total_store_count}</div>` : ""}
			${properties.competitor_brand_count != null ? `<div>Competitor Brand Count: ${properties.competitor_brand_count}</div>` : ""}
			${properties.top_competitor_brand ? `<div>Top Competitor: ${properties.top_competitor_brand}</div>` : ""}
			${properties.Office_Name || properties.office_name ? `<div>Office: ${properties.Office_Name || properties.office_name}</div>` : ""}
			${properties.city ? `<div>City: ${properties.city}</div>` : ""}
			${properties.score_display ? `<div>Pincode Score: ${properties.score_display}</div>` : ""}
			${properties.region_size ? `<div>Region Size (pins): ${properties.region_size}</div>` : ""}
			${properties.revenue_display ? `<div>Online Revenue: ${properties.revenue_display}</div>` : ""}
			${properties.region_revenue_display ? `<div>Region Revenue: ${properties.region_revenue_display}</div>` : ""}
			${properties.top_pincode_1 ? `<div>Top Demand Pin 1: ${properties.top_pincode_1}</div>` : ""}
			${properties.top_pincode_2 ? `<div>Top Demand Pin 2: ${properties.top_pincode_2}</div>` : ""}
			${properties.top_pincode_3 ? `<div>Top Demand Pin 3: ${properties.top_pincode_3}</div>` : ""}
			${properties.recommended_action ? `<div>Action: ${properties.recommended_action}</div>` : ""}
		`;
	}

	if ([STORE_EBO_LAYER_ID, STORE_MBO_LAYER_ID, STORE_LFS_LAYER_ID].includes(layerId)) {
		return `
			<div><strong>${String(properties.store_kind || "store").toUpperCase()} Point</strong></div>
			<div>Pincode: ${properties.pincode || "-"}</div>
			<div>City/State: ${properties.city || "-"}, ${properties.state || "-"}</div>
			<div>Revenue: ${properties.store_revenue ?? "-"}</div>
			<div>Plot Source: ${properties.plot_source || "-"}</div>
		`;
	}

	if (layerId === COMPETITOR_LAYER_ID) {
		return `
			<div><strong>${properties.name || "Competitor store"}</strong></div>
			<div>Brand: ${properties.brand_display || "-"}</div>
			<div>Address: ${properties.formatted_address || "-"}</div>
			<div>Pincode: ${properties.pincode || "-"}</div>
			<div>Status: ${properties.is_operational ? "Operational" : "Unknown"}</div>
		`;
	}

	return "<div>Map item</div>";
};

const getHoveredItem = (layerId, properties = {}) => {
	if (layerId === MASTER_FILL_LAYER_ID) {
		return {
			layerKey: "MASTER_POLYGONS",
			name: properties.office_name || properties.Office_Name || properties.pincode || "Master polygon",
			pincode: properties.pincode || properties.Pincode || "-",
			state: properties.state || properties.State || "-",
		};
	}

	if (layerId === ONLINE_FILL_LAYER_ID) {
		return {
			layerKey: "ONLINE_POLYGONS",
			name: "Online polygon",
			pincode: properties.pincode || "-",
			state: properties.state || "-",
		};
	}

	if (layerId === SKATER_FILL_LAYER_ID) {
		return {
			layerKey: "SKATER_POLYGONS",
			name: properties.region_id || "SKATER polygon",
			pincode: properties.pincode || "-",
			state: properties.state || "-",
		};
	}

	if ([STORE_EBO_LAYER_ID, STORE_MBO_LAYER_ID, STORE_LFS_LAYER_ID].includes(layerId)) {
		return {
			layerKey: layerId,
			name: `${String(properties.store_kind || "").toUpperCase()} point`,
			pincode: properties.pincode || "-",
			state: properties.state || "-",
		};
	}

	return {
		layerKey: "COMPETITOR_POINTS",
		name: properties.brand_display || properties.name || "Competitor point",
		pincode: properties.pincode || "-",
		state: properties.state || "-",
	};
};

const MapView = ({
	filterObj,
	masterPolygonData,
	onlinePolygonData,
	skaterPolygonData,
	storePointData,
	competitorPointData,
	worldStatus,
	setWorldStatus,
	masterStatus,
	onlineStatus,
	skaterStatus,
	storeStatus,
	competitorStatus,
	selectedStatesForMap,
	onMapRendered,
}) => {
	const mapContainerRef = useRef(null);
	const mapRef = useRef(null);
	const popupRef = useRef(null);
	const [mapReady, setMapReady] = useState(false);
	const [hoveredItem, setHoveredItem] = useState(null);

	const activePalette = HUE_PALETTES.find((palette) => palette.key === filterObj.palette) ?? HUE_PALETTES[0];
	const activeColorMode = COLOR_MODES.find((mode) => mode.key === filterObj.colorMode) ?? COLOR_MODES[0];
	const renderedMasterData = useMemo(() => buildMasterRenderData(masterPolygonData), [masterPolygonData]);
	const renderedOnlineData = useMemo(() => buildOnlineRenderData(onlinePolygonData), [onlinePolygonData]);
	const renderedSkaterData = useMemo(
		() => buildSkaterRenderData(skaterPolygonData, filterObj.colorMode, activePalette.colors),
		[activePalette.colors, filterObj.colorMode, skaterPolygonData],
	);

	const stateBoundaryData = useMemo(() => {
		const features = masterPolygonData?.features ?? [];
		if (!features.length) return EMPTY_FEATURE_COLLECTION;
		// Group features by state, keep one representative feature per state for border rendering
		const byState = new Map();
		features.forEach((feature) => {
			const state = feature?.properties?.state || feature?.properties?.State || "UNKNOWN";
			if (!byState.has(state)) byState.set(state, []);
			byState.get(state).push(feature);
		});
		return {
			type: "FeatureCollection",
			features: [...byState.entries()].map(([state, stateFeatures]) => ({
				type: "Feature",
				properties: { state },
				geometry: {
					type: "GeometryCollection",
					geometries: stateFeatures.map((f) => f.geometry).filter(Boolean),
				},
			})),
		};
	}, [masterPolygonData]);

	const loadingStages = [
		{ key: "world", label: "World map", status: worldStatus },
		{ key: "master", label: "Master polygons", status: masterStatus },
		{ key: "online", label: "Online polygons", status: onlineStatus },
		{ key: "skater", label: "SKATER polygons", status: skaterStatus },
		{ key: "store", label: "Store points", status: storeStatus },
		{ key: "competitor", label: "Competitor points", status: competitorStatus },
	];

	const hoveredLabel = hoveredItem
		? `${hoveredItem.name} | ${hoveredItem.state ?? hoveredItem.layerKey} | pincode ${hoveredItem.pincode ?? "-"}`
		: "Hover a polygon or point to inspect the currently visible map data.";

	useEffect(() => {
		if (mapRef.current || !mapContainerRef.current) {
			return;
		}

		const map = new maplibregl.Map({
			container: mapContainerRef.current,
			style: createBaseMapStyle(),
			center: MAP_DEFAULT_VIEW.center,
			zoom: MAP_DEFAULT_VIEW.zoom,
			minZoom: 3,
			maxZoom: 11,
			maxBounds: INDIA_MAP_BOUNDS,
			attributionControl: false,
		});

		map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");
		map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-left");
		popupRef.current = new maplibregl.Popup({
			closeButton: false,
			closeOnClick: false,
			offset: 10,
		});

		map.on("load", () => {
			setWorldStatus("ready");

			map.addSource(MASTER_SOURCE_ID, { type: "geojson", data: EMPTY_FEATURE_COLLECTION });
			map.addSource(ONLINE_SOURCE_ID, { type: "geojson", data: EMPTY_FEATURE_COLLECTION });
			map.addSource(SKATER_SOURCE_ID, { type: "geojson", data: EMPTY_FEATURE_COLLECTION });
			map.addSource(STATE_BORDER_SOURCE_ID, { type: "geojson", data: EMPTY_FEATURE_COLLECTION });
			map.addSource(STORE_SOURCE_ID, { type: "geojson", data: EMPTY_FEATURE_COLLECTION });
			map.addSource(COMPETITOR_SOURCE_ID, { type: "geojson", data: EMPTY_FEATURE_COLLECTION });

			map.addLayer({
				id: "plain-labels",
				type: "raster",
				source: "plain-labels",
				paint: {
					"raster-opacity": 1,
					"raster-saturation": -1,
					"raster-contrast": 0.22,
				},
			});

			map.addLayer({
				id: MASTER_FILL_LAYER_ID,
				type: "fill",
				source: MASTER_SOURCE_ID,
				paint: {
					"fill-color": ["coalesce", ["get", "render_fill"], "#dbeafe"],
					"fill-opacity": 0.08,
				},
			});
			map.addLayer({
				id: MASTER_LINE_LAYER_ID,
				type: "line",
				source: MASTER_SOURCE_ID,
				paint: {
					"line-color": ["coalesce", ["get", "render_line"], "#1f2937"],
					"line-opacity": 0.4,
					"line-width": 0.55,
				},
			});

			map.addLayer({
				id: ONLINE_FILL_LAYER_ID,
				type: "fill",
				source: ONLINE_SOURCE_ID,
				paint: {
					"fill-color": ["coalesce", ["get", "render_fill"], "#d4d4d8"],
					"fill-opacity": 0.48,
				},
			});
			map.addLayer({
				id: ONLINE_LINE_LAYER_ID,
				type: "line",
				source: ONLINE_SOURCE_ID,
				paint: {
					"line-color": ["coalesce", ["get", "render_line"], "#3f3f46"],
					"line-opacity": 0.72,
					"line-width": 0.75,
				},
			});

			map.addLayer({
				id: SKATER_FILL_LAYER_ID,
				type: "fill",
				source: SKATER_SOURCE_ID,
				paint: {
					"fill-color": ["coalesce", ["get", "render_fill"], "#5b8def"],
					"fill-opacity": 0.62,
				},
			});
			map.addLayer({
				id: SKATER_LINE_LAYER_ID,
				type: "line",
				source: SKATER_SOURCE_ID,
				paint: {
					"line-color": ["coalesce", ["get", "render_line"], "#1f2937"],
					"line-opacity": 0.88,
					"line-width": ["coalesce", ["get", "render_line_width"], 0.95],
				},
			});

			map.addLayer({
				id: STATE_BORDER_LAYER_ID,
				type: "line",
				source: STATE_BORDER_SOURCE_ID,
				paint: {
					"line-color": "#111827",
					"line-opacity": 0.78,
					"line-width": 1.25,
				},
			});

			map.addLayer({
				id: STORE_EBO_LAYER_ID,
				type: "symbol",
				source: STORE_SOURCE_ID,
				filter: ["==", ["get", "store_kind"], "ebo"],
				layout: { "icon-image": "pin-ebo", "icon-size": 0.9, "icon-anchor": "bottom", "icon-allow-overlap": true },
			});
			map.addLayer({
				id: STORE_MBO_LAYER_ID,
				type: "symbol",
				source: STORE_SOURCE_ID,
				filter: ["==", ["get", "store_kind"], "mbo"],
				layout: { "icon-image": "pin-mbo", "icon-size": 0.9, "icon-anchor": "bottom", "icon-allow-overlap": true },
			});
			map.addLayer({
				id: STORE_LFS_LAYER_ID,
				type: "symbol",
				source: STORE_SOURCE_ID,
				filter: ["==", ["get", "store_kind"], "lfs"],
				layout: { "icon-image": "pin-lfs", "icon-size": 0.9, "icon-anchor": "bottom", "icon-allow-overlap": true },
			});

			map.addLayer({
				id: COMPETITOR_LAYER_ID,
				type: "symbol",
				source: COMPETITOR_SOURCE_ID,
				layout: {
					"icon-image": ["concat", "comp-", ["get", "brand_display"]],
					"icon-size": 0.9,
					"icon-anchor": "center",
					"icon-allow-overlap": true,
				},
			});

			const interactiveLayerIds = [
				MASTER_FILL_LAYER_ID,
				ONLINE_FILL_LAYER_ID,
				SKATER_FILL_LAYER_ID,
				STORE_EBO_LAYER_ID,
				STORE_MBO_LAYER_ID,
				STORE_LFS_LAYER_ID,
				COMPETITOR_LAYER_ID,
			];

			interactiveLayerIds.forEach((layerId) => {
				map.on("mouseenter", layerId, () => {
					map.getCanvas().style.cursor = "pointer";
				});

				map.on("mousemove", layerId, (event) => {
					const feature = event.features?.[0];
					if (!feature) return;
					const properties = feature.properties ?? {};
					setHoveredItem(getHoveredItem(layerId, properties));
					popupRef.current?.setLngLat(event.lngLat).setHTML(getPopupHtml(layerId, properties)).addTo(map);
				});

				map.on("mouseleave", layerId, () => {
					map.getCanvas().style.cursor = "";
					setHoveredItem(null);
					popupRef.current?.remove();
				});
			});

			setMapReady(true);
		});

		mapRef.current = map;

		return () => {
			setMapReady(false);
			popupRef.current?.remove();
			map.remove();
			mapRef.current = null;
		};
	}, [setWorldStatus]);

	useEffect(() => {
		const map = mapRef.current;
		if (!mapReady || !map) return;
		map.getSource(MASTER_SOURCE_ID)?.setData(renderedMasterData);
		map.setLayoutProperty(MASTER_FILL_LAYER_ID, "visibility", filterObj.layers.MASTER_POLYGONS ? "visible" : "none");
		map.setLayoutProperty(MASTER_LINE_LAYER_ID, "visibility", filterObj.layers.MASTER_POLYGONS ? "visible" : "none");
	}, [filterObj.layers.MASTER_POLYGONS, mapReady, renderedMasterData]);

	useEffect(() => {
		const map = mapRef.current;
		if (!mapReady || !map) return;
		map.getSource(ONLINE_SOURCE_ID)?.setData(renderedOnlineData);
		map.setLayoutProperty(ONLINE_FILL_LAYER_ID, "visibility", filterObj.layers.ONLINE_POLYGONS ? "visible" : "none");
		map.setLayoutProperty(ONLINE_LINE_LAYER_ID, "visibility", filterObj.layers.ONLINE_POLYGONS ? "visible" : "none");
	}, [filterObj.layers.ONLINE_POLYGONS, mapReady, renderedOnlineData]);

	useEffect(() => {
		const map = mapRef.current;
		if (!mapReady || !map) return;
		map.getSource(SKATER_SOURCE_ID)?.setData(renderedSkaterData);
		map.setLayoutProperty(SKATER_FILL_LAYER_ID, "visibility", filterObj.layers.SKATER_POLYGONS ? "visible" : "none");
		map.setLayoutProperty(SKATER_LINE_LAYER_ID, "visibility", filterObj.layers.SKATER_POLYGONS ? "visible" : "none");
	}, [filterObj.layers.SKATER_POLYGONS, mapReady, renderedSkaterData]);

	useEffect(() => {
		const map = mapRef.current;
		if (!mapReady || !map) return;
		map.getSource(STATE_BORDER_SOURCE_ID)?.setData(stateBoundaryData);
		map.setLayoutProperty(STATE_BORDER_LAYER_ID, "visibility", filterObj.layers.STATE_BORDERS ? "visible" : "none");
	}, [filterObj.layers.STATE_BORDERS, mapReady, stateBoundaryData]);

	useEffect(() => {
		const map = mapRef.current;
		if (!mapReady || !map) return;
		const features = storePointData?.features ?? [];
		registerStoreImages(map, features).then(() => {
			map.getSource(STORE_SOURCE_ID)?.setData(storePointData ?? EMPTY_FEATURE_COLLECTION);
			map.setLayoutProperty(STORE_EBO_LAYER_ID, "visibility", filterObj.layers.STORE_EBO_POINTS ? "visible" : "none");
			map.setLayoutProperty(STORE_MBO_LAYER_ID, "visibility", filterObj.layers.STORE_MBO_POINTS ? "visible" : "none");
			map.setLayoutProperty(STORE_LFS_LAYER_ID, "visibility", filterObj.layers.STORE_LFS_POINTS ? "visible" : "none");
		});
	}, [
		filterObj.layers.STORE_EBO_POINTS,
		filterObj.layers.STORE_LFS_POINTS,
		filterObj.layers.STORE_MBO_POINTS,
		mapReady,
		storePointData,
	]);

	useEffect(() => {
		const map = mapRef.current;
		if (!mapReady || !map) return;
		const features = competitorPointData?.features ?? [];
		registerCompetitorImages(map, features).then(() => {
			map.getSource(COMPETITOR_SOURCE_ID)?.setData(competitorPointData ?? EMPTY_FEATURE_COLLECTION);
			map.setLayoutProperty(COMPETITOR_LAYER_ID, "visibility", filterObj.layers.COMPETITOR_POINTS ? "visible" : "none");
		});
	}, [competitorPointData, filterObj.layers.COMPETITOR_POINTS, mapReady]);

	useEffect(() => {
		const map = mapRef.current;
		if (!mapReady || !map) return;
		map.fitBounds(INDIA_MAP_BOUNDS, { padding: 18, maxZoom: 6, duration: 0 });
	}, [mapReady]);

	// Reset mapRendered and wait for maplibre idle after any data/filter change
	useEffect(() => {
		const map = mapRef.current;
		if (!mapReady || !map) return;
		onMapRendered?.(false);
		const handleIdle = () => onMapRendered?.(true);
		map.once("idle", handleIdle);
		return () => map.off("idle", handleIdle);
	}, [
		mapReady,
		renderedMasterData,
		renderedOnlineData,
		renderedSkaterData,
		storePointData,
		competitorPointData,
		filterObj,
		stateBoundaryData,
		onMapRendered,
	]);

	return (
		<Box
			sx={{
				position: "relative",
				height: "100%",
				width: "100%",
				border: "1px solid rgba(34,68,58,0.12)",
				background: "#eef2f0",
				overflow: "hidden",
				"& .maplibregl-map": {
					height: "100%",
					width: "100%",
					fontFamily: "inherit",
				},
				"& .maplibregl-ctrl-group": {
					borderRadius: "14px",
					overflow: "hidden",
					boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
				},
				"& .maplibregl-ctrl-group button": {
					width: "32px",
					height: "32px",
				},
				"& .maplibregl-popup-content": {
					padding: "8px 10px",
					fontSize: "12px",
					lineHeight: 1.45,
					color: "#24332f",
				},
				"& .maplibregl-popup-tip": {
					borderTopColor: "#fff",
					borderBottomColor: "#fff",
				},
			}}>
			<Box ref={mapContainerRef} sx={{ height: "100%", width: "100%" }} />

			<MapLoadingPanel stages={loadingStages} />

			<Paper
				elevation={0}
				sx={{
					position: "absolute",
					right: 16,
					bottom: 16,
					zIndex: 1200,
					p: 1.5,
					maxWidth: 460,
					borderRadius: 3,
					background: "rgba(255,255,255,0.92)",
					border: "1px solid rgba(34,68,58,0.12)",
					backdropFilter: "blur(8px)",
				}}>
				<Stack spacing={1}>
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
						<Chip label={`Mode: ${filterObj.colorMode}`} size="small" sx={{ background: "#e5efeb", color: "#204138" }} />
						<Chip
							label={`Subset: ${filterObj.regionSubset}`}
							size="small"
							sx={{ background: "#e5efeb", color: "#204138" }}
						/>
						<Chip
							label={`Master: ${getFeatureCount(masterPolygonData).toLocaleString()}`}
							size="small"
							sx={{ background: "#e5efeb", color: "#204138" }}
						/>
						<Chip
							label={`Online: ${getFeatureCount(onlinePolygonData).toLocaleString()}`}
							size="small"
							sx={{ background: "#e5efeb", color: "#204138" }}
						/>
						<Chip
							label={`SKATER: ${getFeatureCount(skaterPolygonData).toLocaleString()}`}
							size="small"
							sx={{ background: "#e5efeb", color: "#204138" }}
						/>
						<Chip
							label={`Stores: ${getFeatureCount(storePointData).toLocaleString()}`}
							size="small"
							sx={{ background: "#e5efeb", color: "#204138" }}
						/>
						<Chip
							label={`Competitors: ${getFeatureCount(competitorPointData).toLocaleString()}`}
							size="small"
							sx={{ background: "#e5efeb", color: "#204138" }}
						/>
					</Stack>
					<Typography sx={{ color: "#51635d", fontSize: 13 }}>
						Color behavior: {activeColorMode.label}. States in request:{" "}
						{selectedStatesForMap.length ? selectedStatesForMap.join(", ") : "All India"}
					</Typography>
					<Typography sx={{ color: "#6d7a76", fontSize: 12 }}>{hoveredLabel}</Typography>
				</Stack>
			</Paper>
		</Box>
	);
};

export default MapView;
